import React, { useState, useMemo } from "react";
import {
  Kanban as KanbanIcon,
  Calendar,
  Move,
  GripVertical,
} from "lucide-react";
import { CampaignPost, GroupByOption } from "../types/calendar";
import { SocialIcon } from "./SocialIcon";

interface KanbanViewProps {
  posts: CampaignPost[];
  onSelectPost: (post: CampaignPost) => void;
  onUpdatePostField?: (
    postId: string,
    field: GroupByOption,
    newValue: string,
  ) => void;
  isReadOnly?: boolean;
}

export const KanbanView: React.FC<KanbanViewProps> = ({
  posts,
  onSelectPost,
  onUpdatePostField,
  isReadOnly = false,
}) => {
  const [groupBy, setGroupBy] = useState<GroupByOption>("status");
  const [draggedPostId, setDraggedPostId] = useState<string | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);

  // Compute dynamic columns based on groupBy option
  const columns = useMemo(() => {
    if (groupBy === "status") {
      const defaultStatuses = [
        "Idea",
        "Draft",
        "Approved",
        "Scheduled",
        "Published",
      ];
      const presentStatuses = Array.from(
        new Set(posts.map((p) => p.status || "Idea")),
      );
      return Array.from(new Set([...defaultStatuses, ...presentStatuses]));
    }
    if (groupBy === "phase") {
      const defaultPhases = ["Pre-Launch", "Launch", "Post-Launch"];
      const presentPhases = Array.from(
        new Set(posts.map((p) => p.phase || "Other")),
      );
      return Array.from(new Set([...defaultPhases, ...presentPhases]));
    }
    if (groupBy === "platform") {
      return Array.from(new Set(posts.map((p) => p.platform || "Other")));
    }
    if (groupBy === "format") {
      return Array.from(new Set(posts.map((p) => p.format || "Other"))).filter(
        Boolean,
      );
    }
    return [];
  }, [posts, groupBy]);

  // Group posts by column key
  const groupedPosts = useMemo(() => {
    const map: Record<string, CampaignPost[]> = {};
    columns.forEach((col) => {
      map[col] = [];
    });

    posts.forEach((post) => {
      let key = "Other";
      if (groupBy === "status") key = post.status || "Idea";
      else if (groupBy === "phase") key = post.phase || "Other";
      else if (groupBy === "platform") key = post.platform || "Other";
      else if (groupBy === "format") key = post.format || "Other";

      if (!map[key]) map[key] = [];
      map[key].push(post);
    });

    return map;
  }, [posts, columns, groupBy]);

  // Drag and drop event handlers
  const handleDragStart = (e: React.DragEvent, postId: string) => {
    if (isReadOnly) {
      e.preventDefault();
      return;
    }
    e.dataTransfer.setData("text/plain", postId);
    e.dataTransfer.effectAllowed = "move";
    setDraggedPostId(postId);
  };

  const handleDragEnd = () => {
    setDraggedPostId(null);
    setDragOverColumn(null);
  };

  const handleDragOver = (e: React.DragEvent, columnKey: string) => {
    if (isReadOnly) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverColumn !== columnKey) {
      setDragOverColumn(columnKey);
    }
  };

  const handleDragLeave = (e: React.DragEvent, columnKey: string) => {
    if (isReadOnly) return;
    e.preventDefault();
    if (dragOverColumn === columnKey) {
      setDragOverColumn(null);
    }
  };

  const handleDrop = (e: React.DragEvent, columnKey: string) => {
    if (isReadOnly) return;
    e.preventDefault();
    const postId = e.dataTransfer.getData("text/plain") || draggedPostId;
    setDragOverColumn(null);
    setDraggedPostId(null);

    if (postId && onUpdatePostField) {
      onUpdatePostField(postId, groupBy, columnKey);
    }
  };

  return (
    <div
      style={{
        padding: "1.5rem",
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
        flex: 1,
      }}
    >
      {/* Group Control Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "var(--bg-surface)",
          padding: "0.85rem 1.25rem",
          borderRadius: "12px",
          border: "1px solid var(--border-color)",
          flexWrap: "wrap",
          gap: "0.75rem",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <KanbanIcon size={20} style={{ color: "var(--accent-primary)" }} />
          <span
            style={{
              fontSize: "0.9rem",
              fontWeight: 600,
              color: "var(--text-main)",
            }}
          >
            Group Board By:
          </span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
          {(["status", "phase", "platform", "format"] as GroupByOption[]).map(
            (opt) => (
              <button
                key={opt}
                onClick={() => setGroupBy(opt)}
                style={{
                  padding: "0.35rem 0.75rem",
                  borderRadius: "8px",
                  border: `1px solid ${groupBy === opt ? "var(--accent-primary)" : "var(--border-color)"}`,
                  background:
                    groupBy === opt
                      ? "var(--accent-glow)"
                      : "var(--bg-primary)",
                  color:
                    groupBy === opt
                      ? "var(--accent-primary)"
                      : "var(--text-muted)",
                  fontSize: "0.8rem",
                  fontWeight: groupBy === opt ? 600 : 500,
                  textTransform: "capitalize",
                  transition: "all 0.2s ease",
                }}
              >
                {opt}
              </button>
            ),
          )}
        </div>
      </div>

      {/* Drag & Drop Columns Board */}
      <div
        style={{
          display: "flex",
          gap: "1rem",
          overflowX: "auto",
          paddingBottom: "1rem",
          alignItems: "flex-start",
          minHeight: "600px",
        }}
      >
        {columns.map((col) => {
          const colPosts = groupedPosts[col] || [];
          const isTargetColumn = dragOverColumn === col;

          return (
            <div
              key={col}
              onDragOver={(e) => handleDragOver(e, col)}
              onDragLeave={(e) => handleDragLeave(e, col)}
              onDrop={(e) => handleDrop(e, col)}
              style={{
                width: "320px",
                minWidth: "300px",
                background: isTargetColumn
                  ? "var(--accent-glow)"
                  : "var(--bg-surface)",
                borderRadius: "12px",
                border: `2px ${isTargetColumn ? "dashed var(--accent-primary)" : "solid var(--border-color)"}`,
                display: "flex",
                flexDirection: "column",
                maxHeight: "calc(100vh - 240px)",
                transition: "all 0.2s ease",
              }}
            >
              {/* Column Header */}
              <div
                style={{
                  padding: "0.85rem 1rem",
                  borderBottom: "1px solid var(--border-color)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  background: "var(--bg-primary)",
                  borderTopLeftRadius: "10px",
                  borderTopRightRadius: "10px",
                }}
              >
                <h3
                  style={{
                    fontFamily: "var(--font-heading)",
                    fontSize: "0.95rem",
                    fontWeight: 700,
                    color: "var(--text-main)",
                  }}
                >
                  {col}
                </h3>
                <span
                  className="badge"
                  style={{
                    background: "var(--accent-glow)",
                    color: "var(--accent-primary)",
                  }}
                >
                  {colPosts.length}
                </span>
              </div>

              {/* Cards List Drop Area */}
              <div
                style={{
                  padding: "0.85rem",
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.75rem",
                  overflowY: "auto",
                  flex: 1,
                  minHeight: "150px",
                }}
              >
                {colPosts.length === 0 ? (
                  <div
                    style={{
                      padding: "2rem 1rem",
                      textAlign: "center",
                      color: "var(--text-light)",
                      fontSize: "0.8rem",
                      border: "1px dashed var(--border-color)",
                      borderRadius: "8px",
                    }}
                  >
                    Drop posts here to set {groupBy} to "{col}"
                  </div>
                ) : (
                  colPosts.map((post) => {
                    const isBeingDragged = draggedPostId === post.postId;

                    return (
                      <div
                        key={post.postId}
                        draggable={!isReadOnly}
                        onDragStart={(e) => handleDragStart(e, post.postId)}
                        onDragEnd={handleDragEnd}
                        onClick={() => onSelectPost(post)}
                        style={{
                          padding: "0.85rem",
                          borderRadius: "10px",
                          background: "var(--bg-primary)",
                          border: "1px solid var(--border-color)",
                          cursor: isReadOnly ? "pointer" : "grab",
                          opacity: isBeingDragged ? 0.4 : 1,
                          display: "flex",
                          flexDirection: "column",
                          gap: "0.5rem",
                          transition:
                            "transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease",
                          userSelect: "none",
                        }}
                        onMouseEnter={(e) => {
                          if (!isBeingDragged) {
                            e.currentTarget.style.transform =
                              "translateY(-2px)";
                            e.currentTarget.style.boxShadow =
                              "var(--shadow-md)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isBeingDragged) {
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow = "none";
                          }
                        }}
                      >
                        {/* Card Top row */}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.4rem",
                            }}
                          >
                            <GripVertical
                              size={13}
                              style={{
                                color: "var(--text-light)",
                                cursor: "grab",
                              }}
                            />
                            <SocialIcon
                              platform={post.platform}
                              showName={true}
                            />
                          </div>
                          <span
                            style={{
                              fontSize: "0.75rem",
                              color: "var(--text-light)",
                              fontWeight: 600,
                            }}
                          >
                            {post.postId}
                          </span>
                        </div>

                        {/* Title / Hook */}
                        <div
                          style={{
                            fontSize: "0.88rem",
                            fontWeight: 600,
                            color: "var(--text-main)",
                            lineHeight: 1.4,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                          title={
                            post.hook || post.contentPillar || "Social Post"
                          }
                        >
                          {post.hook || post.contentPillar || "Social Post"}
                        </div>

                        {/* Card Footer row */}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginTop: "0.25rem",
                            paddingTop: "0.5rem",
                            borderTop: "1px solid var(--border-color)",
                            fontSize: "0.75rem",
                            color: "var(--text-muted)",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.3rem",
                            }}
                          >
                            <Calendar size={13} />
                            <span>{post.date || "No Date"}</span>
                          </div>
                          {post.format && (
                            <span
                              style={{
                                padding: "0.1rem 0.4rem",
                                borderRadius: "4px",
                                background: "var(--bg-surface)",
                                border: "1px solid var(--border-color)",
                                fontSize: "0.7rem",
                              }}
                            >
                              {post.format}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
